import { useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { formatLanguageLabel } from '@/util/languageOptions';
import { isRequired } from '@/util/validation/constants';

const INVOICE_LOCALES = [
  'en-US',
  'pt-BR',
  'es-ES',
  'ar-AR',
  'da-DK',
  'nl-NL',
  'fi-FI',
  'fr-FR',
  'de-DE',
  'he-IL',
  'it-IT',
  'ja-JP',
  'no-NO',
  'sv-SE',
];

/** Converts a full locale code (e.g. `en-US`) to a native language label, falling back to the base language code. */
const formatLocaleLabel = (locale: string): string => {
  const lower = locale.toLowerCase();
  const label = formatLanguageLabel(lower);
  return label !== lower ? label : formatLanguageLabel(lower.split('-')[0]);
};

const invoiceLanguageOptions = INVOICE_LOCALES.map((locale) => ({
  value: locale,
  label: formatLocaleLabel(locale),
}));

type FormValues = { invoiceLanguage: string };
const FORM_ID = 'invoice-language-form';

export const InvoiceLanguageFieldset = () => {
  const { t } = useTranslation();
  const { canWriteBilling, customer, updateCustomer, updateCustomerStatus } = useBilling();

  const form = useForm<FormValues>({
    mode: 'onChange',
    values: { invoiceLanguage: customer?.preferred_locales?.[0] ?? 'en-US' },
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form;

  const onSubmit = useCallback(
    async ({ invoiceLanguage }: FormValues) =>
      updateCustomer({ preferred_locales: [invoiceLanguage] }),
    [updateCustomer]
  );

  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1.5 text-sm font-medium text-foreground-muted">
        {t('billing.invoice_title')}
      </h2>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateCustomerStatus === 'pending'}
            disabled={!isDirty}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <FormField
              control={control}
              name="invoiceLanguage"
              rules={isRequired}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('field.language')}</FormLabel>
                  <FormControl>
                    <PopoverCombobox
                      options={invoiceLanguageOptions}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={!canWriteBilling}
                      className="disabled:cursor-not-allowed disabled:text-foreground disabled:opacity-50"
                    />
                  </FormControl>
                  <FormDescription>{t('billing.invoice_subtitle')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};
